import { Loadable } from '@/components/loaders';
import { setRegularPagination } from '@/features/campaigns/campaignsSlice';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { FrontTable, TablePager } from '@/lib';
import { RootState } from '@/reducers';
import { useLazyGetCampaignsQuery } from '@/services/endpoints/campaigns';
import { useGetCampaignsCountQuery } from '@/services/endpoints/campaigns/endpoints';
import { CampaignResponse } from '@/services/endpoints/campaigns/types';
import { Row } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getCampaignsTableColumns } from './helper';

const MINIMUM_LOADING_DURATION = 400;

export const CampaignsTable: FC = () => {
  const { t } = useTranslation('campaign');
  const workspace = useWorkspace();
  const history = useHistory();

  const [getCampaigns, { data, isFetching: isCampaignsFetching, isLoading: isCampaignsLoading, isUninitialized: isCampaignsUninitialized }] =
    useLazyGetCampaignsQuery();
  const [campaigns, setCampaigns] = useState<CampaignResponse[]>([]);

  const searchText = useSelector((state: RootState) => state.campaigns.ui.regular.searchText);
  const validSearchText = searchText && searchText.length > 2 ? searchText : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetCampaignsCountQuery({
    workspaceId: workspace.id,

    searchText: validSearchText,
  });

  const isLoadingDelayed = useDelayedBoolean(isCampaignsLoading, MINIMUM_LOADING_DURATION);
  const shouldShowLoading = isCampaignsLoading || isLoadingDelayed || isCampaignsUninitialized;
  const isFetching = isCampaignsFetching;

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getCampaigns({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
      }),
    [validSearchText]
  );

  const columns = useMemo(() => getCampaignsTableColumns(t), [t]);

  const handleRowClick = (row: Row<CampaignResponse>) => {
    history.push(`${RouteConstants.campaigns}/${row.original.id}`);
  };

  return (
    <TablePager
      stateSelector={(state) => state.campaigns.ui.regular.pagination}
      stateUpdater={setRegularPagination}
      query={queryCallback}
      queryResponse={data ? data.items : undefined}
      setItems={setCampaigns}
      totalCount={totalCount}
      querying={isTotalCountFetching || isFetching}
      wrapperClassNames='mb-0'
    >
      {(pager) => (
        <Loadable isLoading={shouldShowLoading}>
          <FrontTable tableData={campaigns} columns={columns} footer={pager} onRowClick={handleRowClick} notFound={t('table.noCampaignFound')} />
        </Loadable>
      )}
    </TablePager>
  );
};
